<template>
  <div class="share-container">
    <h2>访问分享文件</h2>
    <div v-if="loading" class="loading">加载中...</div>
    <div v-else-if="requiresPassword">
      <label>请输入密码：</label>
      <input v-model="password" type="password" placeholder="输入分享密码" />
      <button type="button" @click="verifyPassword">验证</button>
      <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
    </div>
    <div v-else-if="file">
      <p>文件：{{ file.originalName }} ({{ formatFileSize(file.size) }})</p>
      <p v-if="file.expiresAt">有效期至：{{ formatDate(file.expiresAt) }}</p>
      <div v-if="isVideo" class="video-player">
        <video controls :src="fileSrc" style="max-width: 100%; max-height: 500px;" crossorigin="anonymous" @error="handleVideoError" @loadeddata="console.log('Video loaded successfully')">
        您的浏览器不支持视频播放。
        </video>
      </div>
      <div v-else-if="file.isImage" class="image-preview">
        <img :src="fileSrc" alt="图片预览" style="max-width: 100%; max-height: 500px;" />
      </div>
      <div v-else-if="file.isText" class="text-preview">
        <pre>{{ textContent }}</pre>
      </div>
      <div class="file-actions">
        <button type="button" @click="downloadFile">下载文件</button>
      </div>
    </div>
    <div v-else>
      <p class="error">{{ errorMessage || '分享链接无效或已过期' }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';

const route = useRoute();
const token = route.params.token;
const password = ref('');
const requiresPassword = ref(false);
const file = ref(null);
const errorMessage = ref('');
const loading = ref(true);
const textContent = ref('');

const axiosInstance = axios.create({
  baseURL: 'https://a.410121.xyz/api',
  timeout: 10000,
});

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
};

const fileSrc = computed(() => {
  if (!file.value) return '';
  const url = new URL(`https://a.410121.xyz/api/share/${token}`);
  if (password.value) url.searchParams.append('password', password.value);
  url.searchParams.append('_t', Date.now());
  return url.toString();
});
const mimeMap = {
  'application/mp4': 'video/mp4',
  'video/mp4': 'video/mp4',
};
const isVideo = computed(() => {
  const mappedMime = mimeMap[file.value?.mimeType] || file.value?.mimeType;
  return file.value?.isVideo || mappedMime?.startsWith('video/');
});


const verifyPassword = async () => {
  try {
    console.log('Verifying password for token:', token);
    const response = await axiosInstance.post(`/api/share/verify/${token}`, { password: password.value });
    console.log('Verify response:', response.data);
    if (response.data.code === 200) {
      file.value = { ...response.data.data, expiresAt: file.value?.expiresAt, isVideo: file.value?.isVideo, isImage: file.value?.isImage, isText: file.value?.isText };
      requiresPassword.value = false;
      errorMessage.value = '';
      // 如果是文本文件，加载内容
      if (file.value.isText) {
        await loadTextContent();
      }
    } else {
      errorMessage.value = response.data.message || '密码错误';
    }
  } catch (error) {
    console.error('Password verification failed:', error);
    errorMessage.value = error.response?.data?.message || '验证失败，请稍后重试';
  }
};

const loadTextContent = async () => {
  try {
    const response = await axiosInstance.get(`/api/share/${token}`, {
      params: password.value ? { password: password.value } : {},
      responseType: 'text',
    });
    textContent.value = response.data;
  } catch (error) {
    console.error('Failed to load text content:', error);
    errorMessage.value = '无法加载文本内容';
  }
};

const downloadFile = () => {
  const params = password.value ? `?password=${encodeURIComponent(password.value)}` : '';
  window.open(`https://a.410121.xyz/api/share/download/${token}${params}`, '_blank');
};

const loadShareInfo = async () => {
  try {
    console.log('Loading share info for token:', token);
    const response = await axiosInstance.get(`/share/info/${token}`);
    console.log('Share info response:', response.data);
    if (response.data.code === 200) {
      requiresPassword.value = response.data.data.requiresPassword;
      file.value = {
        ...response.data.data,
        isVideo: response.data.data.mimeType.startsWith('video/'),
        isImage: response.data.data.mimeType.startsWith('image/'),
        isText: response.data.data.mimeType === 'text/plain',
      };
      if (file.value.size === 0) {
        console.warn('文件大小为 0，可能文件不存在:', response.data.data);
        errorMessage.value = '文件大小为 0，可能文件已丢失';
      } else {
        errorMessage.value = '';
        if (file.value.isText && !requiresPassword.value) {
          await loadTextContent();
        }
      }
    } else {
      errorMessage.value = response.data.message || '分享链接无效';
    }
  } catch (error) {
    console.error('Load share info failed:', error);
    errorMessage.value = error.response?.data?.message || '加载分享信息失败，请稍后重试';
  } finally {
    loading.value = false;
  }
};

loadShareInfo();
</script>

<style scoped>
.share-container {
  max-width: 600px;
  margin: 50px auto;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  text-align: center;
}
.share-container input {
  padding: 8px;
  margin: 10px 0;
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.share-container button {
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin: 5px;
}
.share-container button:hover {
  background-color: #0056b3;
}
.error {
  color: #dc3545;
  margin-top: 10px;
}
.loading {
  color: #007bff;
  font-style: italic;
}
.video-player, .image-preview, .text-preview {
  margin: 20px 0;
}
.text-preview pre {
  background-color: #f8f9fa;
  padding: 10px;
  border-radius: 4px;
  white-space: pre-wrap;
  max-height: 500px;
  overflow-y: auto;
}
.file-actions {
  margin-top: 10px;
}
</style>